import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Classify Ship and Debit Step
 *
 * Classifies an inbound email to determine if it's a Ship and Debit (SD) claim.
 *
 * Classification is based on configurable rules (in priority order):
 * - Sender patterns: Exact email or domain matches (highest confidence)
 * - Subject patterns: Case-insensitive substring or regex matches
 * - Body patterns: Case-insensitive substring matches (future)
 *
 * The step logs the classification result and returns flags for downstream
 * conditional branching.
 */
export const classifySDStep = defineStepConfig({
  type: "classify_sd",
  name: "Classify Ship and Debit",
  description:
    "Classify inbound email to determine if it's a Ship and Debit claim",
  category: "Transformation",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to classify",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
    isSD: z
      .boolean()
      .describe("Whether the email is classified as a Ship and Debit"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score of the classification (0-1)"),
    classificationReason: z
      .string()
      .describe(
        "Human-readable explanation of why this classification was made",
      ),
  }),

  configSchema: z.object({
    senderPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Array of sender email patterns to match. Defaults to ['shipanddebit@hubbell.ca']",
      ),
    subjectPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Array of subject patterns to match (case-insensitive substring or regex). Defaults to ['vendor debit memo', '\\\\bSD\\\\b']",
      ),
    bodyPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Array of body patterns to match (case-insensitive substring). Future extensibility.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});
